import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Flex,
  Text,
  Button,
  Badge,
  useDisclosure,
  Divider,
} from "@chakra-ui/react";
import { ResourceImage } from "@root/assets/icons/ResourceImage";
import { useTranslation } from "react-i18next";
import AddResourceModal from "@root/components/kpec-admin/faqs/AddResourceModal";
import { useResourcesContext } from "@root/context/resources-context";
import { getFileExtension } from "@root/utils";
import { getLoggedInUser } from "@root/store/selectors/entities";
import { useSelector } from "react-redux";
import { USER_TYPES } from "@root/constants";
import { ModalDelete } from "@root/components/modal-delete";
import { Paginate } from "@root/components/pagination";
import useMediaQ from "@root/hooks/useMediaQ";
import { DownloadIcon } from "@root/components/mylearning/downloadIcon";

const ResourcesTab: React.FC = () => {
  const { t } = useTranslation("common");
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { sm } = useMediaQ();
  const isMobile = !sm;
  const currentUser: any = useSelector(getLoggedInUser);
  const user: any = useSelector(getLoggedInUser);
  console.log("currentUser", currentUser?.role);
  const {
    pageNo,
    pageSize,
    pageCount,
    setPageNo,
    fetchResources,
    resourcesData,
    removeResource,
    addResource,
    addResourcesLoading,
  } = useResourcesContext();

  const payload = useMemo(
    () => ({
      offset: (pageNo - 1) * pageSize,
      limit: pageSize,
    }),
    [pageNo, pageSize]
  );

  const [isDeleteModal, setIsDeleteModal] = useState(false);
  const [resourceToDelete, setResourceToDelete] = useState<any>(null);

  const handleDownload = (resource: any) => {
    window.open(resource?.fileUrl, "_blank");
  };

  const handleRemove = (resource: any) => {
    setResourceToDelete(resource);
    setIsDeleteModal(true);
  };

  const handleDeleteConfirm = () => {
    if (resourceToDelete) {
      removeResource(resourceToDelete.id, () => {
        fetchResources({
          offset: (pageNo - 1) * pageSize,
          limit: pageSize,
        });
      });
      setResourceToDelete(null);
      setIsDeleteModal(false);
      fetchResources(payload);
    }
  };

  const onSubmit = (data: any) => {
    const formData = new FormData();
    formData.append("file", data.file);
    formData.append("title", data?.title);
    formData.append("description", data?.description);
    formData.append("organizationId", user?.organization?.id);
    addResource(formData);
  };

  useEffect(() => {
    if (!addResourcesLoading && addResourcesLoading !== undefined) {
      onClose();
    }
  }, [addResourcesLoading, onClose]);
  const handleDeleteCancel = () => {
    setResourceToDelete(null);
    setIsDeleteModal(false);
    fetchResources(payload);
  };

  useEffect(() => {
    fetchResources(payload);
  }, [pageNo]);

  const handlePageChange = (newPage: number) => {
    setPageNo(newPage);
  };

  return (
    <Box w="100%">
      <Flex justifyContent={"flex-end"}>
        {currentUser?.role === USER_TYPES.ADMIN && (
          <Button
            type="button"
            size="lg"
            alignSelf="flex-end"
            bgColor={"white"}
            color={"#1819B4"}
            border={"1px solid #1819B4"}
            fontWeight={500}
            fontSize={"14px"}
            onClick={onOpen}
            my={8}
          >
            {t("faq.addResource")}
          </Button>
        )}
      </Flex>
      <AddResourceModal
        isOpen={isOpen}
        addResourcesLoading={addResourcesLoading}
        onClose={onClose}
        onSubmit={onSubmit}
      />

      {resourcesData?.map((resource: any) => (
        <Box
          key={resource?.title}
          bg="white"
          borderRadius="12px"
          border="1px solid #CDD6DC"
          overflow="hidden"
          mb={5}
          boxShadow="sm"
        >
          {isMobile ? (
            <Box>
              <Flex>
                <Box
                  w="120px"
                  bg="#F5F7FA"
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                >
                  <ResourceImage />
                </Box>
                <Box p="16px" flex="1" minW={0}>
                  <Text
                    as="h4"
                    fontSize="16px"
                    fontWeight={600}
                    color="#2F2F34"
                    mb={1}
                    noOfLines={1}
                  >
                    {resource?.title}
                  </Text>
                  <Text
                    color="#5C6670"
                    fontSize="14px"
                    fontWeight={400}
                    mb={3}
                    noOfLines={1}
                  >
                    {resource?.description}
                  </Text>
                  <Badge
                    textColor={"#2F2F34"}
                    border="1px solid #CDD6DC"
                    bg={"transparent"}
                    px={3}
                    py={"2px"}
                    fontWeight={500}
                    borderRadius="16px"
                    fontSize="13px"
                    w="fit-content"
                  >
                    {getFileExtension(resource?.fileKey, true)}
                  </Badge>
                </Box>
              </Flex>
              <Divider borderColor="#E6E9EF" />
              <Flex justify="center" p="12px">
                {currentUser?.role === USER_TYPES.ADMIN ? (
                  <Button
                    variant="ghost"
                    color="#1819B4"
                    fontWeight={600}
                    fontSize="16px"
                    onClick={() => handleRemove(resource)}
                    borderRadius="8px"
                    height={"auto"}
                    px={0}
                  >
                    {t("resourcesTab.remove")}
                  </Button>
                ) : (
                  <Button
                    variant="ghost"
                    color="#1819B4"
                    fontWeight={600}
                    fontSize="16px"
                    leftIcon={<DownloadIcon boxSize={4} color="#1819B4" />}
                    onClick={() => handleDownload(resource)}
                    borderRadius="8px"
                    height={"auto"}
                    px={0}
                  >
                    {t("resourcesTab.download")}
                  </Button>
                )}
              </Flex>
            </Box>
          ) : (
            <Flex align="center" p="16px" justify="space-between">
              <Flex align="center" gap={4} minW={0}>
                <Box
                  minW="56px"
                  minH="56px"
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  bg="#F5F7FA"
                  borderRadius="10px"
                >
                  <ResourceImage />
                </Box>
                <Box minW={0}>
                  <Text
                    as="h4"
                    fontSize="16px"
                    fontWeight={600}
                    color="#2F2F34"
                    mb={1}
                    noOfLines={1}
                  >
                    {resource?.title}
                  </Text>
                  <Text
                    color="#5C6670"
                    fontSize="14px"
                    fontWeight={400}
                    mb={2}
                    noOfLines={1}
                  >
                    {resource?.description}
                  </Text>
                  <Badge
                    textColor={"#2F2F34"}
                    border="1px solid #CDD6DC"
                    bg={"transparent"}
                    px={3}
                    py={"2px"}
                    fontWeight={500}
                    borderRadius="16px"
                    fontSize="13px"
                  >
                    {getFileExtension(resource?.fileKey, true)}
                  </Badge>
                </Box>
              </Flex>
              {currentUser?.role === USER_TYPES.ADMIN ? (
                <Button
                  variant="outline"
                  borderColor="#1819B4"
                  color="#1819B4"
                  fontWeight={600}
                  fontSize="14px"
                  onClick={() => handleRemove(resource)}
                  borderRadius="8px"
                  height={"36px"}
                  px={"20px"}
                  py={"6px"}
                  width={"fit-content"}
                  _hover={{ bg: "#F5F7FA" }}
                >
                  {t("resourcesTab.remove")}
                </Button>
              ) : (
                <Button
                  variant="outline"
                  borderColor="#1819B4"
                  color="#1819B4"
                  fontWeight={600}
                  fontSize="14px"
                  leftIcon={<DownloadIcon boxSize={4} color="#1819B4" />}
                  onClick={() => handleDownload(resource)}
                  borderRadius="8px"
                  height={"36px"}
                  px={"20px"}
                  py={"6px"}
                  width={"fit-content"}
                  _hover={{ bg: "#F5F7FA" }}
                >
                  {t("resourcesTab.download")}
                </Button>
              )}
            </Flex>
          )}
        </Box>
      ))}
      <ModalDelete
        isOpen={isDeleteModal}
        onClose={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        title={t("resourcesTab.confirmDelete")}
        description={t("resourcesTab.confirmDeleteDescription")}
        deleteButtonTitle={t("resourcesTab.delete")}
        cancelButtonTitle={t("resourcesTab.cancel")}
      />
      <Paginate
        page={pageNo}
        count={pageCount}
        pageSize={pageSize}
        onPageChange={handlePageChange}
        margin={1}
        variant="ghost"
      />
    </Box>
  );
};

export default ResourcesTab;
